import {
  Organization,
  Portfolio,
  AgentConfig,
  AsanaConfig,
  FieldMapping,
} from '../services/organizationService';
import { DEFAULT_AGENT_CONFIG } from '../constants/defaultAgentConfig';
import { DEFAULT_FIELD_MAPPINGS } from '../constants/defaultFieldMappings';

/**
 * Resolved configuration for a selected org/portfolio combination.
 * This is the single source of truth that the app uses for all operations.
 * 
 * Config settings (agents, Asana PAT, field mappings) resolve: Portfolio → Org → Defaults
 * Content (directors, charter, mission, etc.) lives on the Portfolio directly.
 */
export interface ResolvedConfig {
  /** Resolved IRIS Agent IDs */
  agentConfig: AgentConfig;
  /** Resolved Asana credentials (PAT + workspace ID) — may be undefined if not configured */
  asanaConfig: AsanaConfig | undefined;
  /** Resolved field mappings (fieldName → Asana GID) */
  fieldMappings: FieldMapping[];
  /** The Asana Project GID to use for task operations */
  asanaProjectId: string;
  /** Programs list (from portfolio) */
  programs: string[];
  /** Themes list (from portfolio) */
  themes: string[];
}

/**
 * Resolves the effective configuration for a given portfolio + organization combination.
 * 
 * Resolution chain: Portfolio Override → Organization Config → Global Defaults
 * 
 * @param portfolio - The selected portfolio (undefined if no portfolio selected)
 * @param organization - The parent organization
 * @returns The fully resolved configuration with all fallbacks applied
 */
export function resolveConfig(
  portfolio: Portfolio | undefined,
  organization: Organization
): ResolvedConfig {
  // No portfolio selected — legacy mode (org has directors/charter directly)
  if (!portfolio) {
    return {
      agentConfig: organization.agentConfig || DEFAULT_AGENT_CONFIG,
      asanaConfig: organization.asanaConfig || undefined,
      // COMMENTED OUT: No fallback to defaults — forces orgs to configure field mappings
      fieldMappings: organization.fieldMappings && organization.fieldMappings.length > 0 ? organization.fieldMappings : DEFAULT_FIELD_MAPPINGS,
      asanaProjectId: organization.asanaProject || '',
      programs: organization.programs || [],
      themes: organization.themes || [],
    };
  }

  // Portfolio inherits config (agents, PAT, field mappings) from org
  if (portfolio.inheritConfig) {
    return {
      agentConfig: organization.agentConfig || DEFAULT_AGENT_CONFIG,
      asanaConfig: organization.asanaConfig || undefined,
      fieldMappings: organization.fieldMappings && organization.fieldMappings.length > 0 ? organization.fieldMappings : DEFAULT_FIELD_MAPPINGS,
      asanaProjectId: portfolio.asanaProjectId,
      programs: portfolio.programs || [],
      themes: portfolio.themes || [],
    };
  }

  // Portfolio has its own config — use portfolio overrides with org fallback
  return {
    agentConfig: portfolio.agentConfig || organization.agentConfig || DEFAULT_AGENT_CONFIG,
    asanaConfig: portfolio.asanaConfig || organization.asanaConfig || undefined,
      fieldMappings: portfolio.fieldMappings && portfolio.fieldMappings.length > 0 ? portfolio.fieldMappings : (organization.fieldMappings && organization.fieldMappings.length > 0 ? organization.fieldMappings : DEFAULT_FIELD_MAPPINGS),
    asanaProjectId: portfolio.asanaProjectId,
    programs: portfolio.programs || [],
    themes: portfolio.themes || [],
  };
}

/**
 * Converts a FieldMapping array to a flat map { fieldName: asanaGid }
 * for use in Asana API calls.
 */
export function fieldMappingsToMap(fieldMappings: FieldMapping[]): Record<string, string> {
  return fieldMappings.reduce((map, fm) => {
    map[fm.fieldName] = fm.asanaGid;
    return map;
  }, {} as Record<string, string>);
}